import React, { useState, useEffect, useRef } from 'react';
import './DateRangePicker.css';
import { formatDate, formatDateHour, formatDateMinute, formatDateSecond } from 'src/utils/string/stringUtil';

// CalendarMonth는 DateRangePicker.tsx에서 복사
function getDaysArray(year: number, month: number) {
	const firstDay = new Date(year, month, 1);
	const lastDay = new Date(year, month + 1, 0);
	const prevLastDay = new Date(year, month, 0);
	const days: { day: number, type: 'prev' | 'current' | 'next' }[] = [];
	const firstDayOfWeek = firstDay.getDay();
	const startOffset = firstDayOfWeek;
	for (let i = startOffset; i > 0; i--) {
		days.push({ day: prevLastDay.getDate() - i + 1, type: 'prev' });
	}
	for (let d = 1; d <= lastDay.getDate(); d++) {
		days.push({ day: d, type: 'current' });
	}
	let nextDay = 1;
	while (days.length < 42) {
		days.push({ day: nextDay++, type: 'next' });
	}
	return days;
}

function CalendarMonth({
	year,
	month,
	selected,
	onSelect,
	today,
	label,
	onYMChange,
	onDayDoubleClick,
	yearMonthVisible,
}: {
	year: number;
	month: number;
	selected?: Date;
	onSelect: (date: Date) => void;
	today: Date;
	label: string;
	onYMChange: (type: 'year' | 'month', value: number) => void;
	onDayDoubleClick?: (date: Date) => void;
	yearMonthVisible: boolean;
}) {
	const days = getDaysArray(year, month);
	const weekDays = ['일', '월', '화', '수', '목', '금', '토'];
	const baseYears = Array.from({ length: 200 }, (_, i) => 1900 + i);
	const years = baseYears.includes(year) ? baseYears : [...baseYears, year].sort((a, b) => a - b);
	return (
		<div className="custom-calendar">
			<div className="calendar-label">{label}</div>
			<div className="calendar-header">
				<button type="button" onClick={() => onYMChange('year', year - 1)}>{'≪'}</button>
				<button type="button" onClick={() => onYMChange('month', month === 0 ? 11 : month - 1)}>{'<'}</button>
				<select value={year} onChange={e => onYMChange('year', Number(e.target.value))}>
					{years.map(y => 
						<option key={y} value={y}>{y}년</option>
					)}
				</select>
				<select value={month + 1} onChange={e => onYMChange('month', Number(e.target.value) - 1)}>
					{Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}월</option>)}
				</select>
				<button type="button" onClick={() => onYMChange('month', month === 11 ? 0 : month + 1)}>{'>'}</button>
				<button type="button" onClick={() => onYMChange('year', year + 1)}>{'≫'}</button>
			</div>
			<div className="calendar-grid">
				{weekDays.map((w, i) => (
					<div
						key={w}
						className={`calendar-weekday${i === 0 ? ' sunday' : ''}${i === 6 ? ' saturday' : ''}`}
					>
						{w}
					</div>
				))}
				{days.map((d, i) => {
					let dateObj: Date;
					if (d.type === 'current') {
						dateObj = new Date(year, month, d.day);
					} else if (d.type === 'prev') {
						const prevMonth = month === 0 ? 11 : month - 1;
						const prevYear = month === 0 ? year - 1 : year;
						dateObj = new Date(prevYear, prevMonth, d.day);
					} else {
						const nextMonth = month === 11 ? 0 : month + 1;
						const nextYear = month === 11 ? year + 1 : year;
						dateObj = new Date(nextYear, nextMonth, d.day);
					}
					const isToday = dateObj.toDateString() === today.toDateString();
					const isSelected = selected && dateObj.toDateString() === selected.toDateString();
					const dayOfWeek = dateObj.getDay();
					const isSunday = dayOfWeek === 0;
					const isSaturday = dayOfWeek === 6;
					const className = `calendar-day${isSelected ? ' selected' : ''}${isToday ? ' today' : ''}${isSunday ? ' sunday' : ''}${isSaturday ? ' saturday' : ''}${d.type !== 'current' ? ' out' : ''}`;
					return (
						<button
							key={i}
							className={className}
							onClick={() => onSelect(dateObj)}
							onDoubleClick={onDayDoubleClick ? () => onDayDoubleClick(dateObj) : undefined}
							disabled={d.type !== 'current'}
							style={d.type !== 'current' ? { color: '#bbb', background: '#f4f4f4', cursor: 'default' } : {}}
						>
							{d.day}
						</button>
					);
				})}
			</div>
		</div>
	);
}

interface Props {
	value?: Date;
	onClear?: () => void;
	onChange: (date: Date) => void;
	onReset: () => void;
	onConfirm: (date: Date) => void;
	yearMonthVisible: boolean;
	timeEditable: boolean;
}	

export default function DatePicker(props: Props) {

	const { 
		value,
		onClear,
		onChange,
		onReset,
		onConfirm,
		yearMonthVisible,
		timeEditable
	} = props;

	const hourRef = useRef<HTMLInputElement>(null);
	const minuteRef = useRef<HTMLInputElement>(null);
	const secondRef = useRef<HTMLInputElement>(null);

	const today = new Date();
	const [selected, setSelected] = useState<Date>(value ?? today);
	const [ym, setYM] = useState({ year: (value ?? today).getFullYear(), month: (value ?? today).getMonth() });
	const [hour, setHour] = useState(value ? formatDateHour(value) : '');
	const [minute, setMinute] = useState(value ? formatDateMinute(value) : '');
	const [second, setSecond] = useState(value ? formatDateSecond(value) : '');

	useEffect(() => {
		const effectiveDate = value ?? today;
		setSelected(effectiveDate);
		setYM({ year: effectiveDate.getFullYear(), month: effectiveDate.getMonth() });
		setHour(value ? formatDateHour(value) : '');
		setMinute(value ? formatDateMinute(value) : '');
		setSecond(value ? formatDateSecond(value) : '');
	}, [value]);

	const prevDateRef = useRef<Date | null>(null);
	
	useEffect(() => {		if (!hour || !minute) return; // 초기값이 아닐 때만 실행
		
		const newDate = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), Number(hour), Number(minute), Number(second));
		
		// 이전 날짜와 같으면 스킵
		if (prevDateRef.current?.getTime() === newDate.getTime()) {
			return;
		}
		
		prevDateRef.current = newDate;
		onChange(newDate);
	}, [hour, minute, second, selected]); // ✅ selected 추가

	const rootRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		rootRef.current?.focus();
	}, []);

	const handleRootKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.key !== 'Enter') return;
		const t = e.target as HTMLElement;
		if (t.closest('button') || t.closest('select')) return;
		e.preventDefault();
		e.stopPropagation();
		const newDate = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), Number(hour), Number(minute), Number(second));
		onConfirm(newDate);
	};

	return (
		<div
			ref={rootRef}
			className="custom-range-picker-popup single-calendar-popup"
			tabIndex={0}
			onKeyDown={handleRootKeyDown}
		>
			<div className="calendar-container">
				<CalendarMonth
					year={ym.year}
					month={ym.month}
					selected={selected}
					onSelect={(date) => { 
						const newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), Number(hour), Number(minute), Number(second));
						setSelected(newDate); 
						onChange(newDate); 
					}}
					today={today}
					label=""
					onYMChange={(type, value) => {
						if (type === 'year') setYM({ ...ym, year: value });
						else setYM({
							year: value === 11 && ym.month === 0 ? ym.year - 1 : value === 0 && ym.month === 11 ? ym.year + 1 : ym.year,
							month: value
						});
					}}
					onDayDoubleClick={() => {
						const newDate = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), Number(hour), Number(minute), Number(second));
						onConfirm(newDate);
					}}
					yearMonthVisible={yearMonthVisible}
				/>
			</div>
			{/* 시간 설정 부분 */}
			{timeEditable && (
				<div className="calendar-time">
					<input type="text" 
						size={1}
						className="flex-1 text-center rounded-[4px] border-[1px] px-[6px]"
						ref={hourRef}
						value={hour}
						onChange={(e) => {
							if(e.target.value.length > 2) return;
							setHour(e.target.value);
						}} 
						onClick={() => { hourRef.current?.select(); }}
					/>시
					<input type="text" 
						size={1}
						className="flex-1 text-center rounded-[4px] border-[1px] px-[6px]"
						ref={minuteRef}
						value={minute}
						onChange={(e) => {
							if(e.target.value.length > 2) return;
							setMinute(e.target.value);
						}} 
						onClick={() => { minuteRef.current?.select(); }}
					/>분
					<input type="text" 
						size={1}
						className="flex-1 text-center rounded-[4px] border-[1px] px-[6px]"
						ref={secondRef}
						value={second}
						onChange={(e) => {
							if(e.target.value.length > 2) return;
							setSecond(e.target.value);
						}} 
						onClick={() => { secondRef.current?.select(); }}
					/>초
				</div>
			)}
			<div className="calendar-btns">
				<div className="left-btns">
					<button className="cancel" onClick={() => {
						const newDate = new Date((value ?? today).getFullYear(), (value ?? today).getMonth(), (value ?? today).getDate());
						onConfirm(newDate);
					}}>취소</button>
					{onClear && (
						<button className="cancel" onClick={() => {
							onClear();
						}}>비우기</button>
					)}
				</div>
				<div className="right-btns">
					<button className="reset" onClick={() => {
						const newDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
						onReset();
					}}>오늘</button>
					<button className="confirm" onClick={() => {
						const newDate = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), Number(hour), Number(minute), Number(second));
						onConfirm(newDate);
					}}>확인</button>
				</div>
			</div>
		</div>
	);
} 